import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createBlogPost, deleteBlogPost, listAllBlogPosts, updateBlogPost } from '../api/client';
import { BlogPostInfo } from '../api/types';
import BlogPostForm, { BlogPostFormValues } from './BlogPostForm';
import { Badge } from './components/badge';
import { Button } from './components/button';
import { Card, CardContent } from './components/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/table';

function BlogPostsIdare() {
  const [posts, setPosts] = useState<BlogPostInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BlogPostInfo | null>(null);

  const load = () => {
    setLoading(true);
    listAllBlogPosts()
      .then(setPosts)
      .catch((err) => setError(err instanceof Error ? err.message : 'Yüklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (p: BlogPostInfo) => {
    setEditing(p);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu yazıyı silmek istediğinize emin misiniz?')) return;
    await deleteBlogPost(id);
    load();
  };

  const handleSubmit = async (values: BlogPostFormValues) => {
    if (editing) {
      await updateBlogPost(editing.id, values);
    } else {
      await createBlogPost(values);
    }
    load();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Blog Yazıları</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Blog sayfasında görünen yazıları yönetin.
          </p>
        </div>
        <Button onClick={handleCreate}><Plus className="h-4 w-4" /> Yeni Yazı</Button>
      </div>

      {loading && <p className="text-sm text-[hsl(var(--muted-foreground))]">Yükleniyor...</p>}
      {error && <p className="text-sm text-[hsl(var(--destructive))]">{error}</p>}

      {!loading && !error && (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.title_en}</TableCell>
                    <TableCell className="text-[hsl(var(--muted-foreground))]">{p.slug}</TableCell>
                    <TableCell>
                      <Badge variant={p.is_published ? 'default' : 'muted'}>
                        {p.is_published ? 'Yayında' : 'Taslak'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(p)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <BlogPostForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSubmit={handleSubmit} />
    </div>
  );
}

export default BlogPostsIdare;
